import { formatWithCommas } from '../func/helper';

export const ALLOWED_DECIMAL_SEPARATORS = ['/', '.', ',', '٫', '،'];

export const FILE_UP_FIELD_TYPES = {
  0: { key: 'id', type: 'integer', label: 'کد' },
  1: { key: 'weight', type: 'decimal', label: 'وزن' },
  2: { key: 'wage', type: 'decimal', label: 'اجرت' },
  3: { key: 'price', type: 'integer', label: 'قیمت' },
  4: { key: 'discount', type: 'integer', label: 'تخفیف' },
};

const DIGIT_MAP = {
  '۰': '0',
  '۱': '1',
  '۲': '2',
  '۳': '3',
  '۴': '4',
  '۵': '5',
  '۶': '6',
  '۷': '7',
  '۸': '8',
  '۹': '9',
  '٠': '0',
  '١': '1',
  '٢': '2',
  '٣': '3',
  '٤': '4',
  '٥': '5',
  '٦': '6',
  '٧': '7',
  '٨': '8',
  '٩': '9',
};

const BASE_THOUSAND_SEPARATORS = [
  ',',
  '.',
  '٬', // Arabic thousands separator U+066C
  ' ',
  '\u00A0',
  '\u2000',
  '\u2001',
  '\u2002',
  '\u2003',
  '\u2004',
  '\u2005',
  '\u2006',
  '\u2007',
  '\u2008',
  '\u2009',
  '\u200A',
  '\u202F',
  '\u205F',
  '\u3000',
  '\u061C',
  '\u200E',
  '\u200F',
  '\u202A',
  '\u202B',
  '\u202C',
  '\u202D',
  '\u202E',
];

function toLatinDigits(value) {
  return Array.from(String(value), (ch) => DIGIT_MAP[ch] ?? ch).join('');
}

function getThousandSeparators(decimalSeparator) {
  return BASE_THOUSAND_SEPARATORS.filter((ch) => ch !== decimalSeparator);
}

function stripSeparators(value, separators) {
  let result = value;
  for (const ch of separators) {
    result = result.split(ch).join('');
  }
  return result;
}

function resolveDecimalEquivalents(value, decimalSeparator) {
  // When user picks . or ,, also treat Arabic/Persian decimal marks as the same role
  if (decimalSeparator === '.' || decimalSeparator === ',') {
    return value.split('٫').join(decimalSeparator).split('،').join(decimalSeparator);
  }
  // Persian momayyez ↔ Persian comma
  if (decimalSeparator === '٫') {
    return value.split('،').join('٫');
  }
  if (decimalSeparator === '،') {
    return value.split('٫').join('،');
  }
  return value;
}

function countOccurrences(value, char) {
  if (!char) return 0;
  return value.split(char).length - 1;
}

export function isAllowedDecimalSeparator(separator) {
  return ALLOWED_DECIMAL_SEPARATORS.includes(separator);
}

/**
 * Parse a single file-up cell using an explicit decimal separator and column type.
 * @returns {{ ok: true, value: string } | { ok: false, message: string }}
 */
export function parseFileUpCell(value, columnIndex, decimalSeparator) {
  const field = FILE_UP_FIELD_TYPES[columnIndex];
  if (!field) {
    return { ok: true, value: value == null ? '' : String(value).trim() };
  }

  if (!isAllowedDecimalSeparator(decimalSeparator)) {
    return { ok: false, message: 'جداکننده اعشار نامعتبر است' };
  }

  if (value === null || value === undefined) {
    if (columnIndex === 4) {
      return { ok: true, value: '0' };
    }
    return { ok: false, message: 'مقدار خالی است' };
  }

  let raw = String(value).trim();
  if (raw === '') {
    if (columnIndex === 4) {
      return { ok: true, value: '0' };
    }
    return { ok: false, message: 'مقدار خالی است' };
  }

  raw = toLatinDigits(raw);
  raw = resolveDecimalEquivalents(raw, decimalSeparator);

  // Already-canonical values (e.g. frontend pre-normalized) pass through
  if (field.type === 'integer' && /^-?\d+$/.test(raw)) {
    return { ok: true, value: raw };
  }
  if (field.type === 'decimal' && /^-?\d+(\.\d+)?$/.test(raw)) {
    return { ok: true, value: raw };
  }

  if (field.type === 'integer') {
    if (raw.includes(decimalSeparator)) {
      return { ok: false, message: 'این فیلد نمی‌تواند اعشار داشته باشد' };
    }

    const normalized = stripSeparators(raw, getThousandSeparators(decimalSeparator));

    if (!/^-?\d+$/.test(normalized)) {
      return { ok: false, message: 'مقدار عددی صحیح نامعتبر است' };
    }

    return { ok: true, value: normalized };
  }

  // decimal field (weight, wage)
  // Strip thousand separators first (while decimal sep is still distinct), then map decimal → .
  const decimalCount = countOccurrences(raw, decimalSeparator);
  if (decimalCount > 1) {
    return { ok: false, message: 'بیش از یک جداکننده اعشار در مقدار وجود دارد' };
  }

  let normalized = stripSeparators(raw, getThousandSeparators(decimalSeparator));

  if (decimalCount === 1) {
    normalized = normalized.split(decimalSeparator).join('.');
  }

  if (!/^-?\d+(\.\d+)?$/.test(normalized)) {
    return { ok: false, message: 'مقدار عددی اعشاری نامعتبر است' };
  }

  return { ok: true, value: normalized };
}

/**
 * Parse a tab-split row for file-up.
 * @returns {{ ok: true, row: string[] } | { ok: false, error: object }}
 */
export function parseFileUpRow(arr, decimalSeparator, rowNumber) {
  const productId = arr[0] != null ? String(arr[0]).trim() : '';
  const row = [];

  for (let i = 0; i < arr.length; i++) {
    const cell = arr[i] == null ? '' : String(arr[i]).trim();
    const result = parseFileUpCell(cell, i, decimalSeparator);

    if (!result.ok) {
      const field = FILE_UP_FIELD_TYPES[i];
      return {
        ok: false,
        error: {
          rowNumber,
          productId,
          field: field ? field.key : `col_${i}`,
          fieldLabel: field ? field.label : `ستون ${i}`,
          rawValue: cell,
          message: result.message,
        },
      };
    }

    row.push(result.value);
  }

  return { ok: true, row };
}

/**
 * Format a structured parse error for alert display.
 */
export function formatFileUpParseError(error) {
  return [
    'خطا در پردازش فایل',
    `ردیف: ${error.rowNumber} | کد محصول: ${error.productId || '—'}`,
    `فیلد: ${error.fieldLabel}`,
    `مقدار: «${error.rawValue}»`,
    `علت: ${error.message}`,
  ].join('\n');
}

export function convertPriceToNumber(priceString) {
  // 1) Early return for null/undefined, arrays/objects
  if (priceString === null || priceString === undefined) {
    return { price: '', formatted: '', isDollar: false };
  }
  if (typeof priceString === 'object') {
    return { price: '', formatted: '', isDollar: false };
  }

  // 2) Normalize to string
  if (typeof priceString !== 'string') {
    priceString = String(priceString);
  }
  priceString = priceString.trim();
  if (priceString === '') {
    return { price: '', formatted: '', isDollar: false };
  }

  // 3) Detect dollar sign on original input
  const isDollar = priceString.includes('$');

  // 4) Map Persian/Arabic digits and decimal separator to Latin
  // Includes: Persian ۰-۹, Arabic ٠-٩, and Arabic decimal separator U+066B "٫"
  const map = {
    '۰': '0',
    '۱': '1',
    '۲': '2',
    '۳': '3',
    '۴': '4',
    '۵': '5',
    '۶': '6',
    '۷': '7',
    '۸': '8',
    '۹': '9',
    '٠': '0',
    '١': '1',
    '٢': '2',
    '٣': '3',
    '٤': '4',
    '٥': '5',
    '٦': '6',
    '٧': '7',
    '٨': '8',
    '٩': '9',
    '٫': '.',
  };
  let normalized = Array.from(priceString, (ch) => map[ch] ?? ch).join('');

  // 5) Remove thousand separators, whitespaces (including Unicode) and bidi controls
  const removeList = [
    ',',
    ' ',
    '\u00A0',
    '\u2000',
    '\u2001',
    '\u2002',
    '\u2003',
    '\u2004',
    '\u2005',
    '\u2006',
    '\u2007',
    '\u2008',
    '\u2009',
    '\u200A',
    '\u202F',
    '\u205F',
    '\u3000',
    '٬', // Arabic thousands separator U+066C
    '\u061C',
    '\u200E',
    '\u200F',
    '\u202A',
    '\u202B',
    '\u202C',
    '\u202D',
    '\u202E',
  ];
  for (const ch of removeList) {
    normalized = normalized.split(ch).join('');
  }

  // 6) Keep only digits, dots, and minus; then enforce a single leading minus
  normalized = normalized.replace(/[^0-9.\-]/g, '');

  if (normalized.length === 0) {
    return { price: '', formatted: '', isDollar };
  }

  const hasLeadingMinus = normalized[0] === '-';
  // Remove all '-' then re-add only one at the start if it was leading
  normalized = (hasLeadingMinus ? '-' : '') + normalized.slice(hasLeadingMinus ? 1 : 0).replace(/-/g, '');

  // 7) Allow only a single decimal dot (keep the first, remove the rest)
  if ((normalized.match(/\./g) || []).length > 1) {
    const parts = normalized.split('.');
    normalized = parts.shift() + '.' + parts.join('');
  }

  // 8) Validate not empty or just '-'
  if (normalized === '' || normalized === '-') {
    return { price: '', formatted: '', isDollar };
  }

  // 9) Numeric check and parse
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) {
    return { price: '', formatted: '', isDollar };
  }

  const price = parseFloat(normalized);

  // 10) Formatted display: thousand separators, no decimals (like PHP number_format(..., 0))
  const formatted = formatWithCommas(price);

  return { price, formatted, isDollar };
}
